import { useCallback, useState, useMemo, useRef, useEffect } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import { machineEventsSearch, productionBatchesSearch } from '@reducers/search';
import { NcFileBatchEventType } from '@v2/pages/common/enums';
import moment from 'moment';
import { EventsPerspective, EventState, FetchEventsParams, LoadingState, UseManageNCFileBatchEventsParams } from '@v2/pages/common/components/NCFileBatchEvents/types';
import { ACCORDION_FETCH_TIMEOUT_MS, CYCLE_EVENT_LEVELS, LIMIT, ORDER_BY } from '@v2/pages/common/components/NCFileBatchEvents/constants';
import { NCFileStatisticsTimesType } from '@v2/context/Times/page/interfaces';
import { machineEventCycleStatistics, ncFileStatisticsTimes } from '@reducers/statistics';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { retrieveRawMachineEventData } from '@reducers/machinesV2';
import { RawMachineEventData } from '@interfaces/base';
import { injectConditionalObject, isTabletOrWeb } from '@helpers';
import { useSelector } from 'react-redux';
import { selectSelectedItem } from '@reducers/machines';
import { View } from 'react-native';
import { useManageAccordion } from '@components/settings/components/common/animatedTable/hooks/useManageAccordion';
import { useOptimisticStatisticsUpdate } from '@v2/pages/common/components/NCFileBatchEvents/hooks/useOptimisticStatisticsUpdate';

export const useManageNCFileBatchEvents = ({
  eventType,
  ncFileId,
  productionBatchId,
  machineIds,
  startedOn,
  endedOn,
  customOrderBy,
  withoutTimeTiles,
}: UseManageNCFileBatchEventsParams) => {
  const dispatch = useAppDispatch();

  const selectedMachine = useSelector(selectSelectedItem);

  const machineIdsExists = !!machineIds;
  const machinesPerspective = machineIdsExists && machineIds?.length > 0;
  const singleMachineEventsMode = machineIdsExists && machineIds?.length === 1;
  const multiMachineEventsMode = machineIdsExists && machineIds?.length > 1;

  const machineIdsHash = machineIds ? machineIds.join('_') : '';

  const eventsPerspective =
    ncFileId != null
      ? EventsPerspective.NC_FILE
      : productionBatchId != null
      ? EventsPerspective.BATCH
      : singleMachineEventsMode
      ? EventsPerspective.SINGLE_MACHINE
      : multiMachineEventsMode
      ? EventsPerspective.MULTI_MACHINES
      : null;

  const [ncFileStatistics, setNcFileStatistics] = useState<NCFileStatisticsTimesType | null>(null);
  const { handleOptimisticUpdate, refetchStatistics } = useOptimisticStatisticsUpdate({
    eventsPerspective,
    ncFileId,
    setNcFileStatistics,
  });

  const isCycleEventType = CYCLE_EVENT_LEVELS.includes(eventType);
  const isTraceEventType = eventType === NcFileBatchEventType.TRACE;

  const [skip, setSkip] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [orderBy, setOrderBy] = useState<string>(customOrderBy || ORDER_BY);
  const [hasMoreCycleEvents, setHasMoreCycleEvents] = useState<boolean>(true);

  // Ref to prevent multiple simultaneous loads
  const isLoadingMoreRef = useRef(false);

  // Per-row abort controllers for accordion subrow fetches
  const subrowAbortControllersRef = useRef<Record<string | number, AbortController>>({});

  // State management
  const [eventState, setEventState] = useState<EventState>({
    cycleActiveChangeEvents: [],
    setupEvents: [],
    traceEvents: [],
  });

  const [loadingState, setLoadingState] = useState<LoadingState>({
    isLoading: true,
    error: null,
  });

  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);

  // Accordion state management via Redux
  const { expandedRows, toggleAccordion: reduxToggleAccordion, loadComplete, resetAll } = useManageAccordion();

  const eventTypeFlags = useMemo(
    () => ({
      shouldLoadCycleEvents: isCycleEventType,
      shouldLoadSetupEvents: eventType === NcFileBatchEventType.SETUP,
      shouldLoadTraceEvents: isTraceEventType,
    }),
    [eventType]
  );

  // Memoize memoizedODataFilter to prevent unnecessary effect re-runs
  const memoizedODataFilter = useMemo(() => {
    if (singleMachineEventsMode || multiMachineEventsMode) {
      return machineIds.map((id) => `MachineId eq ${id}`).join(' or ');
    } else if (productionBatchId != null) {
      return `ProductionBatchId eq ${productionBatchId}`;
    } else if (ncFileId != null) {
      return `NcFileId eq ${ncFileId}`;
    }
    return null;
  }, [singleMachineEventsMode, multiMachineEventsMode, machineIdsHash, productionBatchId, ncFileId]);

  // Ref to track if initial fetch has been done for current params
  const fetchKeyRef = useRef<string>('');

  // Data fetching logic
  const fetchCycleEvents = useCallback(
    async ({ skip = 0, signal, currentOrderBy }: FetchEventsParams) => {
      const timeRange = {
        ...injectConditionalObject(!!startedOn, { from: startedOn }),
        ...injectConditionalObject(!!endedOn, { until: endedOn }),
      };

      const response = await dispatch(
        machineEventCycleStatistics(
          {
            ...injectConditionalObject(ncFileId != null, { ncFileId }),
            ...injectConditionalObject(productionBatchId != null, { productionBatchId }),
            ...injectConditionalObject(singleMachineEventsMode, { machineId: machineIds?.[0] }),
            ...injectConditionalObject(Object.keys(timeRange).length > 0, { timeRange }),
            orderBy: [currentOrderBy || orderBy],
            pagination: {
              includeCount: false,
              pageNumber: skip / LIMIT + 1,
              pageSize: LIMIT,
            },
          },
          signal
        )
      );

      if (response?.cycles) {
        setEventState((prev) => ({
          ...prev,
          cycleActiveChangeEvents: skip === 0 ? response.cycles : [...prev.cycleActiveChangeEvents, ...response.cycles],
        }));

        // Check if we received fewer items than requested, indicating no more data
        if (!signal.aborted && response.cycles.length < LIMIT) {
          setHasMoreCycleEvents(false);
        }
      }

      return response;
    },
    [orderBy, ncFileId, productionBatchId, singleMachineEventsMode, machineIdsHash, startedOn, endedOn]
  );

  const fetchSetupEvents = useCallback(
    async ({ skip = 0, filter, signal, currentOrderBy }: FetchEventsParams) => {
      const dateFilter = [startedOn ? `StartedOn ge ${startedOn}` : null, endedOn ? `EndedOn lt ${endedOn}` : null].filter(Boolean).join(' and ');

      const combinedFilter = [filter, dateFilter].filter(Boolean).join(' and ');

      const response = await dispatch(
        productionBatchesSearch({
          data: {
            skip,
            top: LIMIT,
          },
          filter: {
            orderBy: [currentOrderBy || orderBy],
            oDataFilter: combinedFilter || null,
          },
          resetHistory: true,
          ignoreHistory: true,
          signal,
        })
      );

      if (response?.searchResult) {
        setEventState((prev) => ({
          ...prev,
          setupEvents: skip === 0 ? response.searchResult : [...prev.setupEvents, ...response.searchResult],
        }));
        if (response.totalResultCount !== undefined) {
          setTotalCount(response.totalResultCount);
        }
      }

      return response;
    },
    [orderBy, startedOn, endedOn]
  );

  const fetchTraceEvents = useCallback(
    async ({ skip = 0, filter, signal, currentOrderBy }: FetchEventsParams) => {
      const dateFilter = [startedOn ? `StartedOn ge ${startedOn}` : null, endedOn ? `EndedOn lt ${endedOn}` : null].filter(Boolean).join(' and ');

      const combinedFilter = [filter, dateFilter].filter(Boolean).join(' and ');

      const response = await dispatch(
        machineEventsSearch(
          {
            skip,
            top: LIMIT,
            oDataFilter: combinedFilter || null,
            orderBy: [currentOrderBy || orderBy],
          },
          signal
        )
      );

      if (response?.searchResult) {
        setEventState((prev) => ({
          ...prev,
          traceEvents: skip === 0 ? response.searchResult : [...prev.traceEvents, ...response.searchResult],
        }));
        if (response.totalResultCount !== undefined) {
          setTotalCount(response.totalResultCount);
        }
      }

      return response;
    },
    [orderBy, startedOn, endedOn]
  );

  const fetchTraceEventsForSequenceNumbers = useCallback(
    async (params: { sequenceNumbers?: number[]; productionBatchId?: number; rowKey: string | number; signal: AbortSignal }) => {
      const { sequenceNumbers, productionBatchId: batchId, rowKey, signal } = params;

      // Return early if no valid params provided
      if (!sequenceNumbers?.length && batchId == null) return;

      try {
        let filterCondition: string;

        if (eventType === NcFileBatchEventType.SETUP && batchId != null) {
          // For SETUP events, filter by ProductionBatchId
          filterCondition = `ProductionBatchId eq ${batchId} and Status eq 'Setup'`;
        } else if (sequenceNumbers?.length) {
          // For other event types, filter by sequence numbers
          filterCondition = sequenceNumbers.map((num) => `SequenceNumber eq ${num}`).join(' or ');
          // Add status filter based on eventType
          const statusFilter =
            eventType === NcFileBatchEventType.ACTIVE || eventType === NcFileBatchEventType.CHANGE ? ` and Status eq '${eventType}'` : '';
          filterCondition = `(${filterCondition})${statusFilter}`;
        } else {
          return;
        }

        // Don't combine with existing memoizedODataFilter for SETUP events using ProductionBatchId
        const combinedFilter =
          eventType === NcFileBatchEventType.SETUP && batchId != null
            ? filterCondition
            : memoizedODataFilter
            ? `(${memoizedODataFilter}) and (${filterCondition})`
            : filterCondition;

        const response = await dispatch(
          machineEventsSearch(
            {
              skip: 0,
              top: 1000, // Get all trace events for these sequence numbers or batch
              oDataFilter: combinedFilter,
              orderBy: [customOrderBy || ORDER_BY],
            },
            signal
          )
        );

        if (response?.searchResult) {
          return response.searchResult; // Return data instead of storing locally
        }
        return [];
      } catch (error) {
        console.error('Error loading trace events:', error);
        return [];
      }
    },
    [orderBy, memoizedODataFilter, eventType]
  );

  const fetchRawMachineEvents = useCallback(async (params: { sequenceNumbers?: number[]; machineId?: number; rowKey: string | number; signal: AbortSignal }) => {
    const { sequenceNumbers, machineId, rowKey, signal } = params;

    // Return early if no valid params provided
    if (!sequenceNumbers?.length || machineId == null) return;

    try {
      // Create an array of promises for each sequence number
      const fetchPromises = sequenceNumbers.map((sequenceNumber) =>
        dispatch(
          retrieveRawMachineEventData(
            {
              machineEventSequenceNumber: sequenceNumber,
              machineId,
            },
            signal
          )
        ).then((response) => ({ sequenceNumber, response: response?.rawMachineEvents?.[0] }))
      );

      // Wait for all promises to settle
      const results = await Promise.allSettled(fetchPromises);

      // Process results and return data
      const rawEventsData: Record<number, RawMachineEventData> = {};
      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value?.response) {
          const { sequenceNumber, response } = result.value;
          rawEventsData[sequenceNumber] = response;
        }
      });

      return rawEventsData; // Return data instead of storing locally
    } catch (error) {
      console.error('Error loading raw machine events:', error);
      return {};
    }
  }, []);

  const toggleAccordion = useCallback(
    ({
      rowKey,
      productionBatchId,
      sequenceNumbers,
      machineId,
    }: {
      rowKey: string | number;
      sequenceNumbers?: number[];
      productionBatchId?: number;
      machineId?: number;
    }) => {
      const isCurrentlyExpanded = expandedRows[rowKey]?.isExpanded;

      // Abort any in-flight fetch for this row (covers both close and reopen-while-loading)
      subrowAbortControllersRef.current[rowKey]?.abort();
      delete subrowAbortControllersRef.current[rowKey];

      // Toggle via Redux
      reduxToggleAccordion(rowKey);

      // If opening (wasn't expanded before), fetch data
      if (!isCurrentlyExpanded) {
        const controller = new AbortController();
        subrowAbortControllersRef.current[rowKey] = controller;

        (async () => {
          const timeoutId = setTimeout(() => controller.abort(), ACCORDION_FETCH_TIMEOUT_MS);

          try {
            const results = await Promise.allSettled([
              fetchTraceEventsForSequenceNumbers({
                sequenceNumbers,
                productionBatchId,
                rowKey,
                signal: controller.signal,
              }),
              isCycleEventType
                ? fetchRawMachineEvents({ sequenceNumbers, machineId, rowKey, signal: controller.signal })
                : Promise.resolve(null),
            ]);

            const traceEventsResult = results[0];
            const rawEventsResult = results[1];

            const traceEvents = traceEventsResult.status === 'fulfilled' ? traceEventsResult.value : [];
            const rawEvents = rawEventsResult.status === 'fulfilled' ? rawEventsResult.value : null;

            loadComplete(rowKey, {
              traceEvents,
              rawMachineEvents: rawEvents,
            });
          } catch (error) {
            console.error('Error loading accordion data:', error);
            loadComplete(rowKey, { traceEvents: [], rawMachineEvents: null });
          } finally {
            clearTimeout(timeoutId);
            if (subrowAbortControllersRef.current[rowKey] === controller) {
              delete subrowAbortControllersRef.current[rowKey];
            }
          }
        })();
      }
      // If closing, Redux handles it automatically
    },
    [expandedRows, reduxToggleAccordion, fetchTraceEventsForSequenceNumbers, fetchRawMachineEvents, isCycleEventType, loadComplete]
  );

  const eventsTableTop = useRef<number>(0);
  const eventsTableRef = useRef<View | null>(null);

  useEffect(() => {
    eventsTableRef.current?.measureInWindow((_x, y, _width, _height) => {
      eventsTableTop.current = y;
    });
  }, []);

  useFocusEffect(
    useCallback(() => {
      if (ncFileId == null) return;
      (async () => {
        const ncFileStatistics: NCFileStatisticsTimesType = await dispatch(
          ncFileStatisticsTimes({
            ncFileId,
          })
        );
        if (ncFileStatistics) {
          setNcFileStatistics(ncFileStatistics);
        }
      })();
    }, [ncFileId])
  );

  // Main data fetching effect
  useFocusEffect(
    useCallback(() => {
      // Create unique key for current fetch params to prevent duplicate fetches
      const currentFetchKey = `${eventType}-${memoizedODataFilter}-${orderBy}-${startedOn}-${endedOn}`;

      // Skip if we already fetched with these exact params
      if (fetchKeyRef.current === currentFetchKey) {
        return;
      }
      fetchKeyRef.current = currentFetchKey;

      const abortController = new AbortController();

      const loadEvents = async () => {
        setLoadingState({
          isLoading: true,
          error: null,
        });

        // Reset pagination state
        setSkip(0);
        setHasMoreCycleEvents(true);

        try {
          const { shouldLoadCycleEvents, shouldLoadSetupEvents, shouldLoadTraceEvents } = eventTypeFlags;

          const params: FetchEventsParams = { filter: memoizedODataFilter, signal: abortController.signal, skip: 0 };

          let response;
          if (shouldLoadCycleEvents) {
            response = await fetchCycleEvents(params);
          } else if (shouldLoadSetupEvents) {
            response = await fetchSetupEvents(params);
          } else if (shouldLoadTraceEvents) {
            response = await fetchTraceEvents(params);
          }

          // Only mark as loaded if we actually received data or confirmed there's no data
          const receivedData =
            (shouldLoadCycleEvents && response?.cycles?.length >= 0) ||
            (shouldLoadSetupEvents && response?.searchResult !== undefined) ||
            (shouldLoadTraceEvents && response?.searchResult !== undefined);

          if (receivedData) {
            setLoadingState({
              isLoading: false,
              error: null,
            });
          }
        } catch (error) {
          if (!abortController.signal.aborted) {
            console.error('Error loading events:', error);
            setLoadingState({
              isLoading: false,
              error: error as Error,
            });
          }
        }
      };

      loadEvents();

      return () => {
        abortController.abort();
        // Reset fetch key on blur so we fetch fresh data on next focus
        fetchKeyRef.current = '';
      };
    }, [eventType, memoizedODataFilter, orderBy, startedOn, endedOn])
  );

  useFocusEffect(
    useCallback(() => {
      return () => {
        Object.values(subrowAbortControllersRef.current).forEach((c) => c.abort());
        subrowAbortControllersRef.current = {};
        resetAll();
      };
    }, [resetAll])
  );

  useEffect(() => {
    return () => {
      Object.values(subrowAbortControllersRef.current).forEach((c) => c.abort());
      subrowAbortControllersRef.current = {};
    };
  }, []);

  const prevMachineIdRef = useRef<number>(selectedMachine?.machineId);

  // reset data and loading state when selected machine changed to display loader indicator
  useFocusEffect(
    useCallback(() => {
      if (prevMachineIdRef.current == null || prevMachineIdRef.current === selectedMachine.machineId) return;

      setLoadingState({ error: null, isLoading: true });
      setEventState({
        cycleActiveChangeEvents: [],
        setupEvents: [],
        traceEvents: [],
      });

      prevMachineIdRef.current = selectedMachine?.machineId;
    }, [selectedMachine?.machineId])
  );

  // Determine which events to return based on eventType
  const events = useMemo(() => {
    const { shouldLoadCycleEvents, shouldLoadSetupEvents, shouldLoadTraceEvents } = eventTypeFlags;

    if (shouldLoadCycleEvents) {
      return eventState.cycleActiveChangeEvents;
    }
    if (shouldLoadSetupEvents) {
      return eventState.setupEvents;
    }
    if (shouldLoadTraceEvents) {
      return eventState.traceEvents;
    }

    return [];
  }, [eventTypeFlags, eventState.cycleActiveChangeEvents, eventState.setupEvents, eventState.traceEvents]);

  // Refresh function
  const refresh = useCallback(async () => {
    setLoadingState({
      isLoading: true,
      error: null,
    });

    try {
      const { shouldLoadCycleEvents, shouldLoadSetupEvents, shouldLoadTraceEvents } = eventTypeFlags;

      const abortController = new AbortController();

      const params: FetchEventsParams = { filter: memoizedODataFilter, signal: abortController.signal, skip };

      if (shouldLoadCycleEvents) {
        await fetchCycleEvents(params);
      } else if (shouldLoadSetupEvents) {
        await fetchSetupEvents(params);
      } else if (shouldLoadTraceEvents) {
        await fetchTraceEvents(params);
      }

      setLoadingState({
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error('Error refreshing events:', error);
      setLoadingState({
        isLoading: false,
        error: error as Error,
      });
    }
  }, [eventTypeFlags, memoizedODataFilter, fetchCycleEvents, fetchSetupEvents, fetchTraceEvents, skip]);

  // Handle sorting changes
  const handleSort = useCallback(
    async (sortParams: { orderBy: Record<string, string> }) => {
      const [orderByField, direction] = Object.entries(sortParams.orderBy)[0];
      const newOrderBy = `${orderByField[0]?.toUpperCase() + orderByField.slice(1)} ${direction}`;

      // If orderBy hasn't changed, do nothing
      if (newOrderBy === orderBy) return;

      setOrderBy(newOrderBy);
      setSkip(0);
      setHasMoreCycleEvents(true);
      setEventState({
        cycleActiveChangeEvents: [],
        setupEvents: [],
        traceEvents: [],
      });

      // Fetch with new orderBy
      const { shouldLoadCycleEvents, shouldLoadSetupEvents, shouldLoadTraceEvents } = eventTypeFlags;
      const abortController = new AbortController();

      setLoadingState({ isLoading: true, error: null });

      const params: FetchEventsParams = { filter: memoizedODataFilter, signal: abortController.signal, skip: 0, currentOrderBy: newOrderBy };

      try {
        if (shouldLoadCycleEvents) {
          await fetchCycleEvents(params);
        } else if (shouldLoadSetupEvents) {
          await fetchSetupEvents(params);
        } else if (shouldLoadTraceEvents) {
          await fetchTraceEvents(params);
        }

        setLoadingState({ isLoading: false, error: null });
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error('Error loading events with new sort:', error);
          setLoadingState({ isLoading: false, error: error as Error });
        }
      }
    },
    [eventTypeFlags, memoizedODataFilter, fetchCycleEvents, fetchSetupEvents, fetchTraceEvents, orderBy]
  );

  // Handle pagination only
  const getNextPage = useCallback(async () => {
    const { shouldLoadCycleEvents } = eventTypeFlags;

    // Don't load more if we haven't loaded initial data yet
    if (!events || events.length < LIMIT) return;

    // Prevent multiple simultaneous loads
    // For cycle events, check hasMoreCycleEvents; for others, check totalCount
    const hasReachedEnd = shouldLoadCycleEvents ? !hasMoreCycleEvents : events.length >= totalCount;

    if (isLoadingMoreRef.current || hasReachedEnd) return;

    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);

    const updatedSkip = skip + LIMIT;
    setSkip(updatedSkip);

    try {
      const { shouldLoadCycleEvents, shouldLoadSetupEvents, shouldLoadTraceEvents } = eventTypeFlags;

      const abortController = new AbortController();

      const params: FetchEventsParams = { filter: memoizedODataFilter, signal: abortController.signal, skip: updatedSkip };

      if (shouldLoadCycleEvents) {
        await fetchCycleEvents(params);
      } else if (shouldLoadSetupEvents) {
        await fetchSetupEvents(params);
      } else if (shouldLoadTraceEvents) {
        await fetchTraceEvents(params);
      }

      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;
    } catch (error) {
      console.error('Error loading more events:', error);
      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;
    }
  }, [
    eventTypeFlags,
    memoizedODataFilter,
    fetchCycleEvents,
    fetchSetupEvents,
    fetchTraceEvents,
    skip,
    events?.length,
    totalCount,
    hasMoreCycleEvents,
  ]);

  // Removed allowLoadMoreData effect - now using FlatList's onEndReached

  const orderByConfig: Record<string, Partial<Record<NcFileBatchEventType, string>>> = {
    duration: {
      [NcFileBatchEventType.CYCLE]: 'durationTotalInSeconds',
      [NcFileBatchEventType.ACTIVE]: 'durationActiveInSeconds',
      [NcFileBatchEventType.CHANGE]: 'durationChangeInSeconds',
      [NcFileBatchEventType.SETUP]: 'actualSetupTimeInSeconds',
      [NcFileBatchEventType.TRACE]: '',
    },
    start: {
      [NcFileBatchEventType.CYCLE]: 'startedOn',
      [NcFileBatchEventType.ACTIVE]: 'startedOn',
      [NcFileBatchEventType.CHANGE]: 'startedOn',
      [NcFileBatchEventType.SETUP]: 'startedOn',
      [NcFileBatchEventType.TRACE]: 'startedOn',
    },
    quantity: {
      [NcFileBatchEventType.CYCLE]: 'productionBatchQuantityCumulative',
      [NcFileBatchEventType.ACTIVE]: 'productionBatchQuantityCumulative',
      [NcFileBatchEventType.CHANGE]: 'productionBatchQuantityCumulative',
    },
    batch: {
      [NcFileBatchEventType.CYCLE]: 'productionBatchNumber',
      [NcFileBatchEventType.ACTIVE]: 'productionBatchNumber',
      [NcFileBatchEventType.CHANGE]: 'productionBatchNumber',
      [NcFileBatchEventType.SETUP]: 'batchNumber',
      [NcFileBatchEventType.TRACE]: '',
    },
    machine: {
      [NcFileBatchEventType.CYCLE]: 'machineShortName',
      [NcFileBatchEventType.ACTIVE]: 'machineShortName',
      [NcFileBatchEventType.CHANGE]: 'machineShortName',
      [NcFileBatchEventType.SETUP]: 'machineShortName',
      [NcFileBatchEventType.TRACE]: 'machineShortName',
    },
  };

  const shouldDisplayNCFileName = !!singleMachineEventsMode;
  const shouldDisplayQuantity = productionBatchId != null && isCycleEventType;
  const shouldDisplayVersion = eventType !== NcFileBatchEventType.SETUP;
  const shouldDisplayQuantityDelta = eventType !== NcFileBatchEventType.SETUP && (eventsPerspective !== EventsPerspective.BATCH || isTraceEventType);
  const shouldDisplayBatch = ncFileId != null || (machinesPerspective && isTabletOrWeb);
  const shouldDisplayMachine = ncFileId != null || multiMachineEventsMode;
  const shouldShowBarChart = eventType !== NcFileBatchEventType.TRACE;
  const shouldDisplayStatusSubStatusExecutionMode = eventType === NcFileBatchEventType.TRACE;
  const shouldDisplayAccordionTrigger = eventType !== NcFileBatchEventType.TRACE;
  const shouldShowTimeTilesAndCurveLines =
    !isTraceEventType &&
    !withoutTimeTiles &&
    eventsPerspective != null &&
    [EventsPerspective.BATCH, EventsPerspective.NC_FILE].includes(eventsPerspective);

  // Text configuration with getter functions
  type EventItem = (typeof events)[number];

  const createGetter = (field: string | undefined) => {
    if (!field) return () => '';
    return (item: EventItem) => item?.[field] ?? '';
  };

  const formatBatchText =
    (currentEventType: NcFileBatchEventType) =>
    (item: EventItem): string => {
      if (currentEventType === NcFileBatchEventType.TRACE) {
        const startedOn = item?.productionBatchStartedOn;
        const sequenceNumber = item?.productionBatchSequenceNumber;

        if (!startedOn || sequenceNumber == null) return '';

        // Get last 2 digits of year
        const year = moment(startedOn).format('YY');
        // Format sequence number with leading zeros (6 digits)
        const formattedSequence = String(sequenceNumber).padStart(6, '0');

        return `${year}-${formattedSequence}`;
      }

      // For other levels, use the standard getter
      const batchField = orderByConfig.batch?.[currentEventType];
      return batchField ? item?.[batchField] ?? '' : '';
    };

  const getDurationForTraceEvent = (item: EventItem): number => {
    return moment(item?.endedOn).diff(moment(item?.startedOn), 'seconds');
  };

  const textConfig = {
    duration: Object.entries(orderByConfig.duration || {}).reduce((acc, [key, field]) => {
      const eventLevel = key as NcFileBatchEventType;
      acc[eventLevel] = eventLevel === NcFileBatchEventType.TRACE ? getDurationForTraceEvent : createGetter(field);
      return acc;
    }, {} as Record<NcFileBatchEventType, (item: EventItem) => any>),

    start: Object.entries(orderByConfig.start || {}).reduce((acc, [key, field]) => {
      acc[key as NcFileBatchEventType] = createGetter(field);
      return acc;
    }, {} as Record<NcFileBatchEventType, (item: EventItem) => any>),

    quantity: Object.entries(orderByConfig.quantity || {}).reduce((acc, [key, field]) => {
      acc[key as NcFileBatchEventType] = createGetter(field);
      return acc;
    }, {} as Record<NcFileBatchEventType, (item: EventItem) => any>),

    batch: Object.keys(orderByConfig.batch || {}).reduce((acc, key) => {
      const eventLevel = key as NcFileBatchEventType;
      acc[eventLevel] = eventLevel === NcFileBatchEventType.TRACE ? formatBatchText(eventLevel) : createGetter(orderByConfig.batch?.[eventLevel]);
      return acc;
    }, {} as Record<NcFileBatchEventType, (item: EventItem) => any>),

    machine: Object.entries(orderByConfig.machine || {}).reduce((acc, [key, field]) => {
      acc[key as NcFileBatchEventType] = createGetter(field);
      return acc;
    }, {} as Record<NcFileBatchEventType, (item: EventItem) => any>),
  };

  return {
    events,
    isLoading: loadingState.isLoading,
    isLoadingMore,
    error: loadingState.error,
    orderBy,
    orderByConfig,
    textConfig,
    refresh,
    getNextPage,
    handleSort,
    shouldDisplayQuantityDelta,
    shouldDisplayNCFileName,
    shouldDisplayQuantity,
    shouldDisplayBatch,
    shouldDisplayMachine,
    shouldShowBarChart,
    shouldDisplayStatusSubStatusExecutionMode,
    shouldDisplayAccordionTrigger,
    shouldDisplayVersion,
    shouldShowTimeTilesAndCurveLines,
    ncFileStatistics,
    eventsPerspective,
    handleOptimisticUpdate,
    refetchStatistics,
    // Events Table Measurements
    eventsTableTop,
    eventsTableRef,

    // Accordion functionality (Redux-based)
    expandedRows, // Now a Record from Redux, not a Set
    toggleAccordion, // Wrapper function that integrates with Redux
  };
};
